import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { cache } from '@/lib/cache';

export interface Brand {
  id: string;
  name: string;
  created_at: string;
  role?: string;
  favicon_asset_id?: string | null;
  favicon_url?: string | null;
  website?: string | null;
}

export interface BrandContextType {
  brands: Brand[];
  currentBrandId: string | null;
  currentBrand: Brand | null;
  isLoading: boolean;
  isInitializing: boolean;
  fetchBrands: () => Promise<void>;
  createBrand: (websiteUrl: string) => Promise<void>;
  deleteBrand: (brandId: string) => Promise<boolean>;
  renameBrand: (brandId: string, newName: string) => Promise<boolean>;
  selectBrand: (brandId: string) => Promise<void>;
}

export const BrandContext = createContext<BrandContextType | undefined>(undefined);

export const BrandProvider = ({ children }: { children: React.ReactNode }) => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [currentBrandId, setCurrentBrandId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Load cached brands immediately on mount
  useEffect(() => {
    if (user?.id) {
      const cachedBrands = cache.getBrands(user.id);
      if (cachedBrands) {
        setBrands(cachedBrands);
      }
    }
  }, [user?.id]);

  const currentBrand = brands.find(brand => brand.id === currentBrandId) || null;

  // Initialize brand from database on mount - ONLY ONCE per user change
  useEffect(() => {
    if (user?.id && !isInitialized) {
      setIsInitializing(true);
      loadCurrentNavigationFromDB();
      setIsInitialized(true);
    } else if (!user?.id) {
      // Clear brand when user logs out
      setCurrentBrandId(null);
      setIsInitialized(false);
      setIsInitializing(true);
    }
  }, [user?.id, isInitialized]); // Add isInitialized to prevent multiple calls

  // Fetch brands ONLY when user is available and initialized
  useEffect(() => {
    if (user?.id && isInitialized) {
      fetchBrands();
    }
  }, [user?.id, isInitialized]); // Only depend on user.id

  const loadCurrentNavigationFromDB = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('current_navigation')
        .select('brand_id')
        .eq('user_id', user.id)
        .maybeSingle();
        
      if (error) {
        console.error('BrandProvider: Error loading current navigation:', error);
        return;
      }
      
      if (data && data.brand_id) {
        setCurrentBrandId(data.brand_id);
      } else {
        setCurrentBrandId(null);
      }
    } catch (error) {
      console.error('BrandProvider: Error loading current navigation:', error);
    } finally {
      // Mark initialization as complete after loading current brand
      setIsInitializing(false);
    }
  };

  const saveCurrentNavigationToDB = async (brandId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('current_navigation')
        .upsert({
          user_id: user.id,
          brand_id: brandId,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id'
        });
        
      if (error) {
        console.error('BrandProvider: Error saving current navigation:', error);
        toast.error('Failed to save brand selection');
        return false;
      } else {
        return true;
      }
    } catch (error) {
      console.error('BrandProvider: Error saving current navigation:', error);
      toast.error('Failed to save brand selection');
      return false;
    }
  };

  const fetchBrands = async () => {
    if (!user) return;
    
    // Check if we should show loading state
    const cachedBrands = cache.getBrands(user.id);
    const shouldShowLoading = !cachedBrands || cache.isStale(`brands_${user.id}`, 30000);
    
    try {
      if (shouldShowLoading) {
        setIsLoading(true);
      }
      
      // Fetch brands with favicon info using a proper left join
      const { data: ownedBrands, error: ownedBrandsError } = await supabase
        .from('brands')
        .select(`
          id,
          name,
          created_at,
          user_id,
          favicon_asset_id,
          slug,
          updated_at,
          affiliate_brand_id,
          brand_assets!favicon_asset_id (
            file_url
          ),
          brand_data (
            website
          )
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: true });
        
      if (ownedBrandsError) {
        console.error('BrandProvider: Error fetching owned brands:', ownedBrandsError);
        throw ownedBrandsError;
      }
      
      // Then, fetch brands via user_brand_access
      const { data: accessData, error: accessError } = await supabase
        .from('user_brand_access')
        .select('brand_id, role')
        .eq('user_id', user.id);
        
      if (accessError) {
        console.error('BrandProvider: Error fetching user brand access:', accessError);
      }
      
      // Get brands from access table
      let accessibleBrands = [];
      if (accessData && accessData.length > 0) {
        const brandIds = accessData.map(access => access.brand_id);
        
        const { data: accessibleBrandsData, error: accessibleBrandsError } = await supabase
          .from('brands')
          .select(`
            id,
            name,
            created_at,
            user_id,
            favicon_asset_id,
            slug,
            updated_at,
            affiliate_brand_id,
            brand_assets!favicon_asset_id (
              file_url
            ),
            brand_data (
              website
            )
          `)
          .in('id', brandIds)
          .order('created_at', { ascending: true });
          
        if (accessibleBrandsError) {
          console.error('BrandProvider: Error fetching accessible brands:', accessibleBrandsError);
        } else if (accessibleBrandsData) {
          accessibleBrands = accessibleBrandsData.map((brand: any) => {
            const access = accessData.find(a => a.brand_id === brand.id);
            let faviconUrl = null;
            let website = null;
            
            // Extract favicon URL from the joined brand_assets
            if (brand.brand_assets) {
              if (Array.isArray(brand.brand_assets) && brand.brand_assets.length > 0) {
                faviconUrl = brand.brand_assets[0]?.file_url;
              } else if (brand.brand_assets.file_url) {
                faviconUrl = brand.brand_assets.file_url;
              }
            }
            
            // Extract website from brand_data
            if (brand.brand_data) {
              if (Array.isArray(brand.brand_data) && brand.brand_data.length > 0) {
                website = brand.brand_data[0]?.website;
              } else if (brand.brand_data.website) {
                website = brand.brand_data.website;
              }
            }
            
            return {
              id: brand.id,
              name: brand.name,
              created_at: brand.created_at,
              favicon_asset_id: brand.favicon_asset_id,
              role: access?.role || 'member',
              favicon_url: faviconUrl,
              website: website
            };
          });
        }
      }
      
      // Combine owned and accessible brands
      const ownedBrandsWithRole = (ownedBrands || []).map((brand: any) => {
        let faviconUrl = null;
        let website = null;
        
        // Extract favicon URL from the joined brand_assets
        if (brand.brand_assets) {
          if (Array.isArray(brand.brand_assets) && brand.brand_assets.length > 0) {
            faviconUrl = brand.brand_assets[0]?.file_url;
          } else if (brand.brand_assets.file_url) {
            faviconUrl = brand.brand_assets.file_url;
          }
        }
        
        // Extract website from brand_data
        if (brand.brand_data) {
          if (Array.isArray(brand.brand_data) && brand.brand_data.length > 0) {
            website = brand.brand_data[0]?.website;
          } else if (brand.brand_data.website) {
            website = brand.brand_data.website;
          }
        }
        
        return {
          id: brand.id,
          name: brand.name,
          created_at: brand.created_at,
          favicon_asset_id: brand.favicon_asset_id,
          role: 'owner',
          favicon_url: faviconUrl,
          website: website
        };
      });
      
      // Remove duplicates
      const allBrands = [...ownedBrandsWithRole];
      accessibleBrands.forEach(accessBrand => {
        if (!allBrands.find(brand => brand.id === accessBrand.id)) {
          allBrands.push(accessBrand);
        }
      });
      
      setBrands(allBrands);
      
      // Cache the brands data
      cache.setBrands(user.id, allBrands);
      
      // Validate current brand selection
      if (currentBrandId && !allBrands.some(brand => brand.id === currentBrandId)) {
        setCurrentBrandId(null);
        if (user) {
          await supabase
            .from('current_navigation')
            .delete()
            .eq('user_id', user.id);
        }
      }
      
    } catch (error) {
      console.error('BrandProvider: Error in fetchBrands:', error);
      toast.error('Failed to fetch brands');
    } finally {
      setIsLoading(false);
    }
  };

  const deriveBrandNameFromUrl = (url: string): string => {
    try {
      // Normalize URL
      let normalizedUrl = url.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = `https://${normalizedUrl}`;
      }

      const urlObj = new URL(normalizedUrl);
      let domain = urlObj.hostname;
      
      // Remove www. prefix
      domain = domain.replace(/^www\./, '');
      
      // Handle subdomains - special cases
      const parts = domain.split('.');
      if (parts.length > 2) {
        const subdomain = parts[0];
        const mainDomain = parts[1];
        
        // For shop.nike.com -> Nike Shop
        if (subdomain === 'shop') {
          return `${capitalize(mainDomain)} Shop`;
        }
        // For blog.company.com -> Company Blog  
        if (subdomain === 'blog') {
          return `${capitalize(mainDomain)} Blog`;
        }
        // Otherwise just use main domain
        domain = mainDomain + '.' + parts.slice(2).join('.');
      }
      
      // Extract company name (remove TLD)
      const name = domain.split('.')[0];
      return capitalize(name);
    } catch (error) {
      console.error('Error deriving brand name from URL:', error);
      return 'My Brand';
    }
  };

  const capitalize = (str: string): string => {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  const createBrand = async (websiteUrl: string) => {
    if (!user) {
      toast.error('You must be logged in to create a brand.');
      return;
    }

    try {
      setIsLoading(true);
      
      // Normalize URL
      let normalizedUrl = websiteUrl.trim();
      if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
        normalizedUrl = `https://${normalizedUrl}`;
      }

      // Derive brand name from domain
      const brandName = deriveBrandNameFromUrl(normalizedUrl);

      // Create brand record
      const { data: brandData, error: brandError } = await supabase
        .from('brands')
        .insert([{ name: brandName, user_id: user.id }])
        .select()
        .single();

      if (brandError) {
        console.error('Error creating brand:', brandError);
        toast.error('Failed to create brand');
        return;
      }

      // Extract meta description
      let description = '';
      try {
        const { data: metaData } = await supabase.functions.invoke('extract-meta-description', {
          body: { url: normalizedUrl }
        });
        
        if (metaData?.success && metaData?.description) {
          description = metaData.description;
        }
      } catch (error) {
        console.warn('Failed to extract meta description:', error);
        // Continue without description - not a critical failure
      }

      // Create brand_data record with website and description
      const { error: brandDataError } = await supabase
        .from('brand_data')
        .insert([{
          brand_id: brandData.id,
          website: normalizedUrl,
          description: description,
          keywords: [],
          competitors: [],
          personas: [],
          prompts: []
        }]);

      if (brandDataError) {
        console.error('Error creating brand data:', brandDataError);
        // Don't fail the whole operation for this
      }

      // Provision FTE data from template brand
      try {
        const { data: templateResult, error: fteError } = await supabase.rpc(
          'provision_fte_from_template',
          {
            source_brand_id: '4fc6b2bd-dd18-4b38-9954-72a011af3313', // Template brand ID
            target_brand_id: brandData.id
          }
        );

        if (fteError) {
          console.error('Error provisioning FTE data:', fteError);
          // Don't fail brand creation if FTE provisioning fails
        } else if (templateResult && typeof templateResult === 'object' && 'success' in templateResult) {
          console.log('FTE data provisioned successfully:', templateResult);
        }
      } catch (error) {
        console.warn('Failed to provision FTE data:', error);
        // Continue without FTE data - not a critical failure
      }

      toast.success(`Brand created for ${brandName}!`);
      
      // Set the new brand as current and navigate to dashboard
      await selectBrand(brandData.id);
      navigate('/dashboard');
      fetchBrands(); // Refresh brands after creating

    } catch (error) {
      console.error('Error creating brand:', error);
      toast.error('Failed to create brand');
    } finally {
      setIsLoading(false);
    }
  };

  const deleteBrand = async (brandId: string): Promise<boolean> => {
    if (!user) {
      toast.error('You must be logged in to delete a brand.');
      return false;
    }

    try {
      setIsLoading(true);
      
      // First delete related records to avoid foreign key constraint violations
      await supabase.from('api_tokens').delete().eq('brand_id', brandId);
      await supabase.from('brand_data').delete().eq('brand_id', brandId);
      
      // Then delete the brand
      const { error } = await supabase
        .from('brands')
        .delete()
        .eq('id', brandId)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting brand:', error);
        toast.error('Failed to delete brand');
        return false;
      } else {
        toast.success('Brand deleted successfully!');
        fetchBrands(); // Refresh brands after deleting
        return true;
      }
    } catch (error) {
      console.error('Error deleting brand:', error);
      toast.error('Failed to delete brand');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const renameBrand = async (brandId: string, newName: string): Promise<boolean> => {
    if (!user) {
      toast.error('You must be logged in to rename a brand.');
      return false;
    }

    try {
      setIsLoading(true);
      // Let RLS policy determine if user can update (owner or admin role)
      const { error } = await supabase
        .from('brands')
        .update({ name: newName })
        .eq('id', brandId);

      if (error) {
        console.error('Error renaming brand:', error);
        toast.error('Failed to rename brand. You may not have permission.');
        return false;
      } else {
        toast.success('Brand renamed successfully!');
        fetchBrands(); // Refresh brands after renaming
        return true;
      }
    } catch (error) {
      console.error('Error renaming brand:', error);
      toast.error('Failed to rename brand');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const selectBrand = async (brandId: string) => {
    setCurrentBrandId(brandId);
    
    const success = await saveCurrentNavigationToDB(brandId);
    
    if (success) {
      window.dispatchEvent(
        new CustomEvent('brandChanged', { detail: { brandId: brandId } })
      );
    }
  };

  const value = {
    brands,
    currentBrandId,
    currentBrand,
    isLoading,
    isInitializing,
    fetchBrands,
    createBrand,
    deleteBrand,
    renameBrand,
    selectBrand,
  };

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
};

export const useBrandManager = () => {
  const context = useContext(BrandContext);
  if (context === undefined) {
    throw new Error("useBrandManager must be used within a BrandProvider");
  }
  return context;
};
